// Where the build flow meets the backend: the program it creates at Details and
// keeps in step with later edits, the state it hands the Structure, Content and
// Members steps (the course wizard's own), and reopening a program's build
// where it was left.

import { API } from '../../../../../api';
import { getLLAuthHeaders } from '../../../utils/llAuthHeaders';
import { mergeWithLocalProgress } from '../../aimd-v2/AIMDv2Page';
import { createProgramGeneration, hydrateWizardFromBackend, loadBuildState, saveBuildState } from '../../aimd-v2/api';
import { INITIAL_WIZARD_STATE, LOCAL_STORAGE_KEY } from '../../aimd-v2/constants';
import { AIMD_STEPS, type AIMDv2WizardState, type DocumentItem } from '../../aimd-v2/types';
import {
  formBuildState,
  programPatchOf,
  snapshotIn,
  type ProgramFields,
  type TrainingSnapshot
} from './trainingSnapshot';

/**
 * Send the program whatever changed since it was last saved (`held`; null
 * when that is not known, and then everything is sent). Resolves true when
 * something was sent.
 *
 * A rename goes straight to the program: another program of the org with that
 * name (whatever its capitals) is refused with a 409 and its message, and
 * nothing in the same save is kept.
 */
export const syncProgram = async (
  groupId: string,
  held: ProgramFields | null,
  now: ProgramFields
): Promise<boolean> => {
  const { body, launchDate } = programPatchOf(held, now);
  const sends: Promise<unknown>[] = [];
  if (Object.keys(body).length > 0) sends.push(API.patchProgram({ id: groupId, body, ...getLLAuthHeaders() }));
  if (launchDate) {
    sends.push(API.updateGroupLaunchDate({ grpId: groupId, launchDate: now.launchDate, ...getLLAuthHeaders() }));
  }
  await Promise.all(sends);
  return sends.length > 0;
};

/** Keep what the first four steps hold with the program, so Continue on its card reopens there. */
export const saveForm = (groupId: string, snapshot: TrainingSnapshot): Promise<unknown> =>
  API.saveBuildState({ grpId: groupId, data: formBuildState(snapshot) });

/** A source chosen on Details, as the wizard lists the sources a program draws on. */
const knowledgeItem = (id: string): DocumentItem => ({
  id,
  type: 'file',
  title: '',
  fileTypeLabel: 'FILE',
  instructions: '',
  processingStatus: 'indexed',
  isNew: false,
  selected: true
});

/**
 * The wizard's state as the build begins: on Structure, with nothing generated
 * yet (so the step generates it), holding what the first four steps settled.
 */
export const wizardStateFor = (build: {
  groupId: string;
  generationId: string;
  snapshot: TrainingSnapshot;
  /**
   * The program's other learning outcomes, as the generator's learning
   * objectives. Never the capabilities: the backend reads those, with their
   * four levels, and the format from the program itself, above everything else.
   */
  objectives: string[];
}): AIMDv2WizardState => ({
  ...INITIAL_WIZARD_STATE,
  currentStep: AIMD_STEPS.indexOf('structure'),
  highestCompletedStep: AIMD_STEPS.indexOf('preview'),
  // The snapshot rides in here: the wizard saves its requirements whole, so it is kept from now on.
  requirements: formBuildState(build.snapshot).requirements,
  outcomes: { outcomes: build.objectives.map((value) => ({ id: crypto.randomUUID(), value })) },
  documents: { uploadedDocuments: [], previousDocuments: build.snapshot.details.docIds.map(knowledgeItem) },
  preview: { generatedPreview: build.snapshot.preview },
  generationId: build.generationId,
  groupId: build.groupId
});

/** Put the wizard's state where its steps read it from as they mount. */
const holdForWizard = (state: AIMDv2WizardState) => {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // No room, or it cannot be written: the wizard falls back to what the backend holds.
  }
};

/**
 * Start the build: a generation record for the program (already saved with
 * its name and everything it is built from, by syncProgram), and the wizard's
 * state saved for the Structure step to open on.
 */
export const startBuild = async (groupId: string, snapshot: TrainingSnapshot, objectives: string[]): Promise<void> => {
  const { generationId } = await createProgramGeneration(groupId);
  const state = wizardStateFor({ groupId, generationId, snapshot, objectives });
  holdForWizard(state);
  await saveBuildState(groupId, state);
};

/**
 * Reopen a program built in this flow. Resolves its snapshot, or null when the
 * program was not built here. A program whose build is under way also has the
 * wizard's state made ready, at the step it reached.
 */
export const openTraining = async (groupId: string): Promise<TrainingSnapshot | null> => {
  const snapshot = snapshotIn(await loadBuildState(groupId));
  if (snapshot?.step !== 'build') return snapshot;
  const state = mergeWithLocalProgress(await hydrateWizardFromBackend(groupId), groupId);
  // The saved state lists a program's sources by id only: give the steps the ones chosen on Details.
  const noSources = state.documents.uploadedDocuments.length + state.documents.previousDocuments.length === 0;
  holdForWizard(
    noSources
      ? {
          ...state,
          documents: { uploadedDocuments: [], previousDocuments: snapshot.details.docIds.map(knowledgeItem) }
        }
      : state
  );
  return snapshot;
};
